import { DatePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { ActivatedRoute, Router, RouterLink, RouterOutlet } from '@angular/router';
import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import {
    FuseNavigationService,
    FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { CreditsComponent } from 'app/layout/common/credits/credits.component';
import { LanguagesComponent } from 'app/layout/common/languages/languages.component';
import { MessagesComponent } from 'app/layout/common/messages/messages.component';
import { NotificationsComponent } from 'app/layout/common/notifications/notifications.component';
import { QuickChatComponent } from 'app/layout/common/quick-chat/quick-chat.component';
import { SearchComponent } from 'app/layout/common/search/search.component';
import { ShortcutsComponent } from 'app/layout/common/shortcuts/shortcuts.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { Subject, takeUntil } from 'rxjs';
import { UserService } from '../../../../core/user/user.service';
import { FeatureFlagService } from '../../../../core/feature-flag/feature-flag.service';
import { MatFormField, MatOption, MatSelect } from '@angular/material/select';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthService } from '../../../../core/auth/auth.service';
import { BaseService } from '../../../../core/http/base.service';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { ChangePasswordDialogComponent } from '../../../common/user/change-password-dialog/change-password-dialog.component';
import { FuseConfigService, Scheme } from '@fuse/services/config';

@Component({
    selector: 'classic-layout',
    templateUrl: './classic.component.html',
    styles: [
        `
            @keyframes reminder-wiggle {
                0%, 30%, 100% {
                    transform: rotate(0) scale(1);
                }
                5%, 15% {
                    transform: rotate(-18deg) scale(1.2);
                }
                10%, 20% {
                    transform: rotate(18deg) scale(1.2);
                }
                25% {
                    transform: rotate(-8deg) scale(1.1);
                }
            }

            .reminder-icon {
                animation: reminder-wiggle 2s ease-in-out infinite;
            }

            @keyframes reminder-ping {
                0% {
                    transform: scale(1);
                    opacity: 0.9;
                }
                60%, 100% {
                    transform: scale(2.4);
                    opacity: 0;
                }
            }

            .reminder-ping {
                animation: reminder-ping 2s cubic-bezier(0, 0, 0.2, 1) infinite;
            }

            @media (prefers-reduced-motion: reduce) {
                .reminder-icon,
                .reminder-ping {
                    animation: none;
                }
            }
        `,
    ],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        FuseLoadingBarComponent,
        FuseVerticalNavigationComponent,
        MatButtonModule,
        MatIconModule,
        LanguagesComponent,
        CreditsComponent,
        FuseFullscreenComponent,
        SearchComponent,
        ShortcutsComponent,
        MessagesComponent,
        NotificationsComponent,
        UserComponent,
        RouterOutlet,
        RouterLink,
        QuickChatComponent,
        MatSelect,
        MatFormField,
        MatOption,
        TranslocoDirective,
        NgClass,
        DatePipe,
        MatMenuModule,
        NgTemplateOutlet,
    ],
})
export class ClassicLayoutComponent implements OnInit, OnDestroy {
    isScreenSmall: boolean;
    navigation: Navigation;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    scheme: string;
    // Logo animation properties
    showOldLogo: boolean = true;
    showNewLogo: boolean = false;
    logoTransitionStarted: boolean = false;

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _userService: UserService,
        protected readonly authService: AuthService,
        private readonly service: BaseService,
        private _dialog: MatDialog,
         private _snackBar: MatSnackBar,
        private _translocoService: TranslocoService,
         private _fuseConfigService: FuseConfigService,
        private _featureFlagService: FeatureFlagService
    ) {}

    openChangePassword(): void {
        this._dialog.open(ChangePasswordDialogComponent, {
            panelClass: ['w-96']
         }).afterClosed().subscribe((result) => {
            if (result) {
                this._snackBar.open(
                    this._translocoService.translate('auth.change_password.success'),
                    'OK',
                    { duration: 4000 }
                );
            }
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    get showApiKeyReminder(): boolean {
        return (
            this._userService.shops.length > 0 &&
            (this._userService.apiKeyRequiredShops.length > 0 ||
                !this._userService.uzumApiKeys.some((apiKey) => apiKey.status === 'ACTIVE'))
        );
    }

    get showPlanReminder(): boolean {
        return this.planDaysLeft !== null && this.planDaysLeft <= 10;
    }

    get planDaysLeft(): number | null {
        return this._userService.planDaysLeft;
    }

    get planExpireDate(): string | undefined {
        return this._userService.plan?.expireDate;
    }

    get canRenewPlan(): boolean {
        return !this._userService.isTrial && !this._router.url.startsWith('/settings/general/finance');
    }

    get isEmployee(): boolean {
        return this._userService.isEmployee;
    }

    get isWmsEnabled(): boolean {
        return this._featureFlagService.isEnabled('WMS');
    }

    get forcePasswordChange(): boolean {
        return this._userService.forcePasswordChange;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Start logo transition animation after component initializes
        this.startLogoTransition();

        this.service.one<{ordersCount: any[]}>('api/fbs/orders', 'counts').subscribe((fbs) => {
            const ordersCount = fbs?.ordersCount || [];
            const fbsCount = ordersCount.reduce((acc, item) => {
                acc[item.status] = item.counter;
                return acc;
            }, {})
            const navigationComponent =
                this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>('mainNavigation');
            const orderFbsNew = this._fuseNavigationService.getItem('order.fbs.new',navigationComponent.navigation);
            if (orderFbsNew && fbsCount['CREATED']) {
                orderFbsNew.badge = {
                    title: fbsCount['CREATED'],
                    classes: 'px-2 bg-yellow-300 text-black rounded-full'
                };
            }
            const orderFbsPacking = this._fuseNavigationService.getItem('order.fbs.packing',navigationComponent.navigation);
            if (orderFbsPacking && fbsCount['PACKING']) {
                orderFbsPacking.badge = {
                    title: fbsCount['PACKING'],
                    classes: 'px-2 bg-yellow-300 text-black rounded-full'
                };
            }
            //... repeat for other statuses as needed
            const orderFbsSupply = this._fuseNavigationService.getItem('order.fbs.supply',navigationComponent.navigation);
            if (orderFbsSupply && fbsCount['PENDING_DELIVERY']) {
                orderFbsSupply.badge = {
                    title: fbsCount['PENDING_DELIVERY'],
                    classes: 'px-2 bg-yellow-300 text-black rounded-full'
                };
            }
            const orderFbsDelivering = this._fuseNavigationService.getItem('order.fbs.delivering',navigationComponent.navigation);
            if (orderFbsDelivering && fbsCount['DELIVERING']) {
                orderFbsDelivering.badge = {
                    title: fbsCount['DELIVERING'],
                    classes: 'px-2 bg-yellow-300 text-black rounded-full'
                };
            }
            const orderFbsDelivered = this._fuseNavigationService.getItem('order.fbs.delivered',navigationComponent.navigation);
            if (orderFbsDelivered && fbsCount['DELIVERED']) {
                orderFbsDelivered.badge = {
                    title: fbsCount['DELIVERED'],
                    classes: 'px-2 bg-yellow-300 text-black rounded-full'
                };
            }
            const orderFbsAcceptedAtDp = this._fuseNavigationService.getItem('order.fbs.accepted-at-dp',navigationComponent.navigation);
            if (orderFbsAcceptedAtDp && fbsCount['ACCEPTED_AT_DP']) {
                orderFbsAcceptedAtDp.badge = {
                    title: fbsCount['ACCEPTED_AT_DP'],
                    classes: 'px-2 bg-yellow-300 text-black rounded-full'
                };
            }
            const orderFbsDeliveredToCustomer = this._fuseNavigationService.getItem('order.fbs.delivered-to-customer',navigationComponent.navigation);
            if (orderFbsDeliveredToCustomer && fbsCount['DELIVERED_TO_CUSTOMER_DELIVERY_POINT']) {
                orderFbsDeliveredToCustomer.badge = {
                    title: fbsCount['DELIVERED_TO_CUSTOMER_DELIVERY_POINT'],
                    classes: 'px-2 bg-yellow-300 text-black rounded-full'
                };
            }
            const orderFbsCompleted = this._fuseNavigationService.getItem('order.fbs.completed',navigationComponent.navigation);
            if (orderFbsCompleted && fbsCount['COMPLETED']) {
                orderFbsCompleted.badge = {
                    title: fbsCount['COMPLETED'],
                    classes: 'px-2 bg-yellow-300 text-black rounded-full'
                };
            }
            const orderFbsCancelled = this._fuseNavigationService.getItem('order.fbs.cancelled',navigationComponent.navigation);
            if (orderFbsCancelled && fbsCount['CANCELED']) {
                orderFbsCancelled.badge = {
                    title: fbsCount['CANCELED'],
                    classes: 'px-2 bg-yellow-300 text-black rounded-full'
                };
            }
            const orderFbsPendingCancellation = this._fuseNavigationService.getItem('order.fbs.pending-cancellation',navigationComponent.navigation);
            if (orderFbsPendingCancellation && fbsCount['PENDING_CANCELLATION']) {
                orderFbsPendingCancellation.badge = {
                    title: fbsCount['PENDING_CANCELLATION'],
                    classes: 'px-2 bg-yellow-300 text-black rounded-full'
                };
            }
            const orderFbsReturned = this._fuseNavigationService.getItem('order.fbs.returned',navigationComponent.navigation);
            if (orderFbsReturned && fbsCount['RETURNED']) {
                orderFbsReturned.badge = {
                    title: fbsCount['RETURNED'],
                    classes: 'px-2 bg-yellow-300 text-black rounded-full'
                };
            }

            navigationComponent.refresh();


        });
        const fbs = this._activatedRoute.snapshot.data.fbs || {shops:  []};
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                navigation.default = navigation.default.map((item) => {
                    if (!['document', 'settings', 'unit'].includes(item.id)) {
                        item.hidden = (item) => this._userService.isCN;
                    }
                    item.children = item.children.map((child) => {
                        if (child.id === 'shop.card-copy') {
                            child.hidden = (child) => !this._featureFlagService.isEnabled('CARD_TRANSFER');
                        }
                        if (child.id === 'order.fbs') {
                            child.hidden = (item) => fbs.shops.length === 0;
                        }
                        if (['settings.reviews'].includes(child.id)) {
                            child.hidden = (child) => this._userService.isCN;
                        }
                        if (['settings.general'].includes(child.id)) {
                            child.hidden = (child) => this._userService.isEmployee;
                        }
                        if (['settings.invoice-package'].includes(child.id)) {
                            child.hidden = (child) => !this._userService.isCN;
                        }
                        if (['dashboard.hourly-counts'].includes(child.id)) {
                            child.hidden = (child) => this._userService.isCN;
                        }
                        return child;
                    });
                    return item;
                });
                navigation.ya = navigation.ya.map((item) => {
                    item.children = item.children.map((child) => {
                        if (child.id !== 'ya.settings.api-keys') {
                            if (!this._userService.isPremium  && !this._userService.isTrial) {
                                child.disabled = true;
                                child.badge = this.premiumBadge;
                            }
                            if (child.children?.length) {
                                child.children = child.children.map((subChild) => {
                                    if (!this._userService.isPremium && !this._userService.isTrial) {
                                        subChild.disabled = true;
                                        subChild.badge = this.premiumBadge;
                                    }

                                    return subChild;
                                });
                            }
                        }

                        return child;
                    })
                    return item;
                });
                this.navigation = navigation;
            });
        this._featureFlagService.features$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>('mainNavigation')?.refresh());
        this._featureFlagService.load().pipe(takeUntil(this._unsubscribeAll)).subscribe();

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });
        this._fuseConfigService.config$.pipe(takeUntil(this._unsubscribeAll)).subscribe((config: any) => {
            this.scheme = config.scheme;
        });
    }
    private get premiumBadge () {
        return {
            title: 'Premium',
            classes: 'px-2 bg-teal-400 text-black rounded',
        };
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation =
            this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
                name
            );

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    onMarketChanged(value: any) {
        sessionStorage.setItem('market', value);
        this.navigateToMarket();
    }

    navigateToMarket() {
        //todo we should get this from backend in no redirect case
        const redirectURL = sessionStorage.getItem('market') === 'ya' ? '/ya/dashboards' : '/dashboards/home';
        this._router.navigateByUrl(decodeURIComponent(redirectURL), {replaceUrl: true})
            .then(() => {

            });
    }

    /**
     * Start logo transition animation
     */
    startLogoTransition(): void {
        // Wait 2 seconds after component loads, then start transition
        setTimeout(() => {
            this.logoTransitionStarted = true;

            // After 1 second, start fading out old logo and fading in new logo
            setTimeout(() => {
                this.showOldLogo = false;
                this.showNewLogo = true;
            }, 1000);
        }, 2000);
    }
    onChanged($event: any): void {
        const mode = $event.checked ? 'dark' : 'light';
        console.log('Changing UI mode to:', mode);
        this.setScheme(mode);
        localStorage.setItem('ui_mode', mode);
    }
     setScheme(scheme: Scheme): void
    {
        this._fuseConfigService.config = {scheme};
    }
}
